import { z } from "zod/v4";

/**
 * Shared constants for chat assistant message terminations.
 *
 * When a generation ends non-normally, the backend writes a sentinel string as
 * (or appended to) the assistant message text. These sentinels are the source
 * of truth for "this message did not complete normally" and are shared
 * across backend (writer), frontend, and mobile (detectors / retry UI).
 */

/**
 * User clicked Stop — the generation was cancelled via cancel_generation.
 */
export const CANCELLED_RESPONSE_SENTINEL = "_Request cancelled._" as const;

/**
 * Server-side stream watchdog fired (prefill or inter-token inactivity timeout).
 * The user did NOT cancel — surface a "try again" affordance rather than
 * implying the user meant to cancel.
 */
export const TIMEOUT_RESPONSE_SENTINEL =
  "_Response timed out — tap retry._" as const;

/**
 * Server process is draining or restarting. This is expected during K8s
 * rollouts and should surface as retryable rather than as a user cancel.
 */
export const INTERRUPTED_RESPONSE_SENTINEL =
  "_Response interrupted — tap retry._" as const;

/**
 * True if the assistant message ended in a retryable non-normal sentinel.
 * Use for "show retry button" / "treat as failed" UI logic.
 */
export function endsWithCancelOrTimeout(
  message: string | null | undefined,
): boolean {
  if (!message) return false;
  return (
    message.endsWith(CANCELLED_RESPONSE_SENTINEL) ||
    message.endsWith(TIMEOUT_RESPONSE_SENTINEL) ||
    message.endsWith(INTERRUPTED_RESPONSE_SENTINEL)
  );
}

/**
 * Legacy backend-inserted approval copy. The UI owns approval affordances, so
 * assistant message text should not include this manual prompt.
 */
export const CHAT_APPROVAL_INTERRUPT_MESSAGE =
  "Please approve this action to continue." as const;

const CHAT_APPROVAL_INTERRUPT_SUFFIX =
  `\n\n${CHAT_APPROVAL_INTERRUPT_MESSAGE}` as const;

export function isChatApprovalInterruptMessage(message: string): boolean {
  return message.trim() === CHAT_APPROVAL_INTERRUPT_MESSAGE;
}

export function stripChatApprovalInterruptMessage(message: string): string {
  const trimmedRight = message.trimEnd();

  if (trimmedRight === CHAT_APPROVAL_INTERRUPT_MESSAGE) {
    return "";
  }

  if (trimmedRight.endsWith(CHAT_APPROVAL_INTERRUPT_SUFFIX)) {
    return trimmedRight
      .slice(0, -CHAT_APPROVAL_INTERRUPT_SUFFIX.length)
      .trimEnd();
  }

  return message;
}

export const E_ChatAttachmentType = {
  IMAGE: "image",
  PDF: "pdf",
  CSV: "csv",
  SPREADSHEET: "spreadsheet",
} as const;

export const CHAT_IMAGE_UPLOAD_LIMITS = {
  MAX_ATTACHMENTS: 4,
  MAX_ORIGINAL_BYTES: 10_000_000,
  MAX_ORIGINAL_PIXELS: 25_000_000,
  FULL_MAX_DIMENSION: 2048,
  PREVIEW_MAX_DIMENSION: 480,
} as const;

/**
 * Per-type original upload caps. Originals are canonical and immutable;
 * model derivatives are bounded separately by the conversion layer.
 */
export const CHAT_ATTACHMENT_UPLOAD_LIMITS = {
  [E_ChatAttachmentType.IMAGE]: {
    maxBytes: CHAT_IMAGE_UPLOAD_LIMITS.MAX_ORIGINAL_BYTES,
  },
  [E_ChatAttachmentType.PDF]: { maxBytes: 15_000_000 },
  [E_ChatAttachmentType.CSV]: { maxBytes: 2_000_000 },
  [E_ChatAttachmentType.SPREADSHEET]: { maxBytes: 10_000_000 },
} as const;

/** Router-level ceiling; the per-type cap is enforced by the upload spec. */
export const CHAT_ATTACHMENT_MAX_UPLOAD_BYTES = Math.max(
  ...Object.values(CHAT_ATTACHMENT_UPLOAD_LIMITS).map(
    (entry) => entry.maxBytes,
  ),
);

export const CHAT_ATTACHMENT_MODEL_DERIVATIVE_LIMITS = {
  /** Pages of a PDF rendered for the model; later pages are not visible. */
  MAX_PDF_PAGES: 20,
  /** Cap on text derivatives (CSV passthrough and XLSX→CSV projection). */
  MAX_TEXT_BYTES: 200_000,
} as const;

export const CHAT_IMAGE_WEBP_TRANSFORM_VERSION =
  "webp-v2-bun-full2048q80-preview480q70" as const;

export const E_ChatAttachmentPreviewRenderer = {
  IMAGE: "image",
  FILE: "file",
} as const;

export const z_ChatAttachmentType = z.enum([
  E_ChatAttachmentType.IMAGE,
  E_ChatAttachmentType.PDF,
  E_ChatAttachmentType.CSV,
  E_ChatAttachmentType.SPREADSHEET,
]);

export const z_ChatAttachmentPreviewRenderer = z.enum([
  E_ChatAttachmentPreviewRenderer.IMAGE,
  E_ChatAttachmentPreviewRenderer.FILE,
]);

export const z_ChatAttachmentHash = z.string().regex(/^sha256:[a-f0-9]{64}$/);

export const z_ChatImageWebpDerivative = z.object({
  objectPath: z.string().min(1).max(1024),
  hash: z_ChatAttachmentHash,
  mimeType: z.literal("image/webp"),
  byteSize: z.number().int().positive(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

export const z_ChatImageAssetRef = z.object({
  assetCuid: z.string().min(1),
  sourceHash: z_ChatAttachmentHash,
  transformVersion: z.string().min(1),
  full: z_ChatImageWebpDerivative,
  preview: z_ChatImageWebpDerivative,
});

export const z_ChatAttachmentImageMimeType = z.enum([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
export type T_ChatAttachmentImageMimeType = z.infer<
  typeof z_ChatAttachmentImageMimeType
>;

/** Kept as the image-mime alias while V6 call sites migrate. */
export const z_ChatAttachmentMimeType = z_ChatAttachmentImageMimeType;

export const z_ChatAttachmentUploadMimeType = z.enum([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "text/csv",
  "application/csv",
  "text/plain",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);
export type T_ChatAttachmentUploadMimeType = z.infer<
  typeof z_ChatAttachmentUploadMimeType
>;

export const z_ChatAttachmentRef = z.object({
  type: z_ChatAttachmentType,
  objectPath: z.string().min(1).max(1024),
  hash: z_ChatAttachmentHash,
  imageAsset: z_ChatImageAssetRef.optional(),
  fileName: z.string().min(1).max(255).optional(),
  mimeType: z_ChatAttachmentUploadMimeType.optional(),
  byteSize: z.number().int().positive().optional(),
});

export const z_RequestChatAttachmentUploadInput = z.object({
  mimeType: z_ChatAttachmentUploadMimeType,
  byteSize: z.number().int().positive().max(CHAT_ATTACHMENT_MAX_UPLOAD_BYTES),
  hash: z_ChatAttachmentHash,
  fileName: z.string().min(1).max(255).optional(),
});

export const z_GetChatAttachmentPreviewsInput = z.object({
  messageCuid: z.string().min(1),
});

export const z_GetChatAttachmentDownloadUrlInput = z.object({
  messageCuid: z.string().min(1),
  attachmentObjectPath: z.string().min(1).max(1024),
});

export const z_ChatAttachmentPreviewResult = z.object({
  attachmentObjectPath: z.string().min(1).max(1024),
  type: z_ChatAttachmentType,
  renderer: z_ChatAttachmentPreviewRenderer,
  title: z.string().min(1).max(80),
  subtitle: z.string().min(1).max(120).nullable(),
  url: z.string().url().nullable(),
  mimeType: z.string().min(1).max(128).nullable(),
  byteSize: z.number().int().positive().nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});

export const z_ChatAttachmentDownloadUrlResult = z.object({
  attachmentObjectPath: z.string().min(1).max(1024),
  type: z_ChatAttachmentType,
  url: z.string().url(),
  mimeType: z.string().min(1).max(128),
  byteSize: z.number().int().positive().nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  fileExtension: z.string().min(1).max(16),
});

export type T_ChatAttachmentType = z.infer<typeof z_ChatAttachmentType>;
export type T_ChatAttachmentPreviewRenderer = z.infer<
  typeof z_ChatAttachmentPreviewRenderer
>;
export type T_ChatAttachmentMimeType = z.infer<typeof z_ChatAttachmentMimeType>;
export type T_ChatImageAssetRef = z.infer<typeof z_ChatImageAssetRef>;
export type T_ChatImageWebpDerivative = z.infer<
  typeof z_ChatImageWebpDerivative
>;
export type T_ChatAttachmentRef = z.infer<typeof z_ChatAttachmentRef>;
export type T_RequestChatAttachmentUploadInput = z.infer<
  typeof z_RequestChatAttachmentUploadInput
>;
export type T_ChatAttachmentPreviewResult = z.infer<
  typeof z_ChatAttachmentPreviewResult
>;
export type T_ChatAttachmentDownloadUrlResult = z.infer<
  typeof z_ChatAttachmentDownloadUrlResult
>;
